import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import blogPosts from '../data/blogPosts';
import { Helmet } from 'react-helmet-async';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// ─── Page Wrapper ────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #111;
  padding-bottom: 80px;
`;

// ─── Hero Banner ─────────────────────────────────────────────────────────────

const Hero = styled.section`
  position: relative;
  width: 100%;
  height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 0 20px;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  color: #fff;
  margin: 0 0 12px;
  letter-spacing: -0.5px;

  span {
    color: #fe88f1;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  max-width: 480px;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

// ─── Main Content ─────────────────────────────────────────────────────────────

const ContentArea = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 24px 0;
`;

const SectionLabel = styled.p`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #fe88f1;
  margin: 0 0 24px;
`;

// ─── Grid ─────────────────────────────────────────────────────────────────────

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

// ─── Card ─────────────────────────────────────────────────────────────────────

const Card = styled.article`
  background: #1a1a1a;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  border: 1px solid rgba(255, 255, 255, 0.06);

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  }
`;

const CardImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;

  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const CategoryBadge = styled.span`
  position: absolute;
  top: 14px;
  left: 14px;
  background: #fe88f1;
  color: #111;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 4px;
`;

const CardBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CardMeta = styled.p`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.4);
  margin: 0 0 10px;
`;

const CardTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 12px;
  line-height: 1.4;
`;

const CardExcerpt = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.65;
  margin: 0 0 24px;
  flex: 1;
`;

const ReadMore = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #fe88f1;
  text-decoration: none;
  letter-spacing: 0.3px;
  transition: gap 0.2s ease;

  &:hover {
    gap: 10px;
  }

  &::after {
    content: '→';
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const Blogs = () => {
  const [allPosts, setAllPosts] = useState(blogPosts);

  useEffect(() => {
    fetch(`${BACKEND_URL}/posts`)
      .then((r) => r.ok ? r.json() : [])
      .then((dbPosts) => {
        // Merge: MongoDB posts first, then static posts (no duplicates by slug)
        const dbSlugs = new Set(dbPosts.map((p) => p.slug));
        const staticFiltered = blogPosts.filter((p) => !dbSlugs.has(p.slug));
        setAllPosts([...dbPosts, ...staticFiltered]);
      })
      .catch(() => setAllPosts(blogPosts)); // graceful fallback
  }, []);
  return (
    <PageWrapper>
      <Helmet>
        <title>The Cinema Blog — MyCinemaDB</title>
        <meta name="description" content="Explore deep dives, reviews, film history, and conversations about the art of cinema on the MyCinemaDB blog." />
      </Helmet>
      <Hero>
        <HeroContent>
          <HeroTitle>
            The Cinema<span> Blog</span>
          </HeroTitle>
          <HeroSubtitle>
            Deep dives, reviews, history, and conversations about the art of film.
          </HeroSubtitle>
        </HeroContent>
      </Hero>

      <ContentArea>
        <SectionLabel>Latest Articles</SectionLabel>
        <Grid>
          {allPosts.map((post) => {
            // MongoDB posts use _id; static posts use id
            const key = post._id || post.id;
            // MongoDB posts may lack a separate excerpt field—strip HTML and truncate
            const excerpt = post.excerpt ||
              (post.content
                ? post.content.replace(/<[^>]+>/g, '').slice(0, 160) + '…'
                : '');
            return (
              <Card key={key}>
                <CardImageWrapper>
                  <CardImage src={post.coverImage} alt={post.title} loading="lazy" />
                  <CategoryBadge>{post.category}</CategoryBadge>
                </CardImageWrapper>
                <CardBody>
                  <CardMeta>
                    {post.author} &bull; {post.date}
                  </CardMeta>
                  <CardTitle>{post.title}</CardTitle>
                  <CardExcerpt>{excerpt}</CardExcerpt>
                  <ReadMore to={`/blogs/${post.slug}`}>Read Article</ReadMore>
                </CardBody>
              </Card>
            );
          })}
        </Grid>
      </ContentArea>
    </PageWrapper>
  );
};

export default Blogs;
